/**
 * Peças reutilizáveis da interface.
 *
 * Ficam num arquivo só de propósito: são componentes pequenos, sem estado, que
 * quase sempre são usados juntos. Um arquivo por peça aqui geraria dez arquivos
 * de vinte linhas e dez imports em cada página, sem ganho nenhum.
 */

import type { CSSProperties, ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { cn } from '@/lib/cn';
import { PLATFORM_LABEL, STATUS_LABEL, TOURNAMENT_STATUS_LABEL } from '@/lib/format';
import type { TeamDto } from '@/lib/api';

/* ------------------------------------------------------------------ *
 * Identidade do time
 * ------------------------------------------------------------------ */

const CREST_SIZE = {
  sm: 'size-6 text-[0.5rem]',
  md: 'size-9 text-[0.6875rem]',
  lg: 'size-14 text-base',
  xl: 'size-20 text-xl',
} as const;

/**
 * Escudo do time. Sem imagem, cai em um monograma derivado do nome — e a cor
 * do monograma também vem do nome, então cada time tem uma cor estável em vez
 * de todos ficarem cinza iguais.
 */
export function TeamCrest({
  team,
  size = 'md',
  className,
}: {
  team: Pick<TeamDto, 'name' | 'shortName' | 'crestUrl'> | null | undefined;
  size?: keyof typeof CREST_SIZE;
  className?: string;
}) {
  if (!team) {
    return (
      <span
        className={cn(
          CREST_SIZE[size],
          'inline-flex shrink-0 items-center justify-center rounded-md border border-dashed border-line-strong text-faint',
          className,
        )}
        aria-hidden
      >
        ?
      </span>
    );
  }

  if (team.crestUrl) {
    return (
      <img
        src={team.crestUrl}
        alt=""
        loading="lazy"
        className={cn(CREST_SIZE[size], 'shrink-0 rounded-md object-contain', className)}
      />
    );
  }

  const initials = monogram(team.shortName || team.name);
  const hue = hashHue(team.name);

  return (
    <span
      aria-hidden
      className={cn(
        CREST_SIZE[size],
        'ucc-crest inline-flex shrink-0 items-center justify-center rounded-md border font-semibold tracking-tight',
        className,
      )}
      /*
       * Só o matiz sai daqui. Saturação e luminosidade moram no CSS, em
       * `.ucc-crest`, porque elas dependem do tema: o mesmo matiz precisa de
       * fundo escuro com letra clara no tema escuro e o oposto no claro. Fixar
       * as três aqui deixava o monograma preto sobre a página branca.
       */
      style={{ '--crest-hue': hue } as CSSProperties}
    >
      {initials}
    </span>
  );
}

function monogram(name: string): string {
  const words = name
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .split(/\s+/)
    .filter(Boolean);
  if (words.length === 0) return '??';
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return (words[0]![0]! + words[1]![0]!).toUpperCase();
}

function hashHue(input: string): number {
  let hash = 0;
  for (let i = 0; i < input.length; i += 1) hash = (hash * 31 + input.charCodeAt(i)) >>> 0;
  return hash % 360;
}

/** Nome + escudo, o par que aparece em toda tabela e linha de partida. */
export function TeamLabel({
  team,
  size = 'md',
  reverse = false,
  short = false,
  className,
}: {
  team: TeamDto | null | undefined;
  size?: keyof typeof CREST_SIZE;
  reverse?: boolean;
  short?: boolean;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex min-w-0 items-center gap-2.5',
        reverse && 'flex-row-reverse text-right',
        className,
      )}
    >
      <TeamCrest team={team} size={size} />
      <span className="truncate font-medium">
        {team ? (short ? team.shortName : team.name) : 'A definir'}
      </span>
    </span>
  );
}

/* ------------------------------------------------------------------ *
 * Badges
 * ------------------------------------------------------------------ */

export function Badge({
  children,
  tone = 'neutral',
  className,
}: {
  children: ReactNode;
  tone?: 'neutral' | 'gold' | 'win' | 'loss' | 'live' | 'warn';
  className?: string;
}) {
  const tones = {
    neutral: 'border-line-strong bg-surface-2 text-muted',
    gold: 'border-gold/30 bg-gold/10 text-gold-ink',
    win: 'border-win/30 bg-win/10 text-win',
    loss: 'border-loss/30 bg-loss/10 text-loss',
    live: 'border-live/40 bg-live/10 text-live',
    warn: 'border-draw/40 bg-draw/10 text-draw',
  } as const;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-[0.6875rem] font-semibold tracking-wide uppercase',
        tones[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

export function PlatformBadge({ platform }: { platform: keyof typeof PLATFORM_LABEL }) {
  return <Badge>{PLATFORM_LABEL[platform] ?? platform}</Badge>;
}

export function MatchStatusBadge({ status }: { status: keyof typeof STATUS_LABEL }) {
  const tone =
    status === 'live'
      ? 'live'
      : status === 'finished'
        ? 'win'
        : status === 'walkover'
          ? 'loss'
          : status === 'disputed'
            ? 'warn'
            : 'neutral';

  return (
    <Badge tone={tone}>
      {status === 'live' && (
        <span className="size-1.5 animate-pulse rounded-full bg-live" aria-hidden />
      )}
      {STATUS_LABEL[status] ?? status}
    </Badge>
  );
}

export function TournamentStatusBadge({
  status,
}: {
  status: keyof typeof TOURNAMENT_STATUS_LABEL;
}) {
  // Vermelho (`live`) fica reservado para partida acontecendo agora. Um
  // campeonato em fase de grupos está saudável, não em alerta.
  const tone =
    status === 'registration'
      ? 'gold'
      : status === 'group_stage' || status === 'knockout'
        ? 'win'
        : 'neutral';
  return <Badge tone={tone}>{TOURNAMENT_STATUS_LABEL[status] ?? status}</Badge>;
}

/* ------------------------------------------------------------------ *
 * Estrutura de página
 * ------------------------------------------------------------------ */

export function PageShell({ children, className }: { children: ReactNode; className?: string }) {
  // pt-28 abre espaço para o cabeçalho fixo de 4.5rem sem colar o conteúdo nele.
  return (
    <div className={cn('mx-auto w-full max-w-ucc px-6 pt-28 pb-20', className)}>{children}</div>
  );
}

export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
  marca,
  voltar = true,
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  /** Imagem que identifica a pagina — escudo do time, brasao da edicao. */
  marca?: ReactNode;
  /**
   * Seta de voltar. Ligada por padrão: quem entra numa página de detalhe —
   * um campeonato, um time, uma partida — chegou de alguma lista, e voltar
   * para ela é o gesto mais repetido do sistema. Desligue nas páginas que são
   * o começo da navegação (home, painel), onde a seta só teria o histórico do
   * navegador para oferecer.
   */
  voltar?: boolean;
}) {
  return (
    <header className="mb-10 flex flex-wrap items-end justify-between gap-6 border-b border-line pb-7">
      <div className="flex min-w-0 items-center gap-5">
        {voltar && <BotaoVoltar />}
        {marca}
        <div className="min-w-0">
          {eyebrow && <div className="ucc-eyebrow mb-2">{eyebrow}</div>}
          <h1 className="ucc-display text-[clamp(1.9rem,4vw,3rem)]">{title}</h1>
          {description && <p className="mt-2.5 max-w-2xl text-muted">{description}</p>}
        </div>
      </div>
      {/*
          `shrink-0` sem `flex-wrap` era a causa do transbordo horizontal no
          celular: com dois ou tres botoes o bloco chegava a 479px e empurrava a
          pagina inteira para 504px de largura numa tela de 390. Agora ele
          quebra linha, e o `shrink-0` sai — encolher e exatamente o que se
          espera dele quando nao ha espaco.
      */}
      {actions && <div className="flex flex-wrap items-center gap-2.5">{actions}</div>}
    </header>
  );
}

/**
 * Volta uma entrada no histórico.
 *
 * Usa o histórico, e não uma rota fixa, porque a mesma página é alcançada de
 * vários lugares: um time vem da lista de times, da classificação de um grupo
 * ou do card de uma partida, e mandar sempre para `/times` levaria o visitante
 * a um lugar em que ele não estava.
 *
 * Quando não há para onde voltar — link colado, aba nova, primeira página da
 * sessão — a seta some em vez de virar um botão que não faz nada.
 */
function BotaoVoltar() {
  const navegar = useNavigate();
  const podeVoltar = typeof window !== 'undefined' && window.history.length > 1;

  if (!podeVoltar) return null;

  return (
    <button
      type="button"
      onClick={() => navegar(-1)}
      aria-label="Voltar para a página anterior"
      title="Voltar"
      className="grid size-10 shrink-0 place-items-center rounded-lg border border-line-strong text-muted transition-colors hover:border-gold/50 hover:text-gold-ink"
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="size-5"
        aria-hidden
      >
        <path d="M19 12H5" />
        <path d="m12 19-7-7 7-7" />
      </svg>
    </button>
  );
}

export function Panel({
  title,
  aside,
  children,
  className,
  bodyClassName,
}: {
  title?: ReactNode;
  aside?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
}) {
  return (
    <section className={cn('ucc-panel overflow-hidden', className)}>
      {(title || aside) && (
        <header className="flex items-center justify-between gap-4 border-b border-line px-5 py-3.5">
          {title && <h2 className="ucc-display text-lg tracking-wide">{title}</h2>}
          {aside}
        </header>
      )}
      <div className={cn(bodyClassName)}>{children}</div>
    </section>
  );
}

/* ------------------------------------------------------------------ *
 * Estados
 * ------------------------------------------------------------------ */

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="px-6 py-14 text-center">
      <p className="ucc-display text-xl text-paper">{title}</p>
      {description && <p className="mx-auto mt-2 max-w-md text-sm text-muted">{description}</p>}
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('animate-pulse rounded-md bg-surface-2', className)} aria-hidden />;
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const message =
    error instanceof Error ? error.message : 'Não foi possível carregar estes dados.';
  return (
    <div className="px-6 py-14 text-center" role="alert">
      <p className="ucc-display text-xl text-loss">Deu problema aqui</p>
      <p className="mx-auto mt-2 max-w-md text-sm text-muted">{message}</p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="mt-6 rounded-lg border border-line-strong px-5 py-2.5 text-sm font-semibold transition-colors hover:border-gold/50"
        >
          Tentar de novo
        </button>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Navegação por abas
 * ------------------------------------------------------------------ */

export function Tabs<T extends string>({
  tabs,
  active,
  onChange,
  className,
}: {
  /** `alerta`: pendências que pedem ação -- aparece em destaque, e some quando é zero. */
  tabs: ReadonlyArray<{ id: T; label: string; count?: number; alerta?: number }>;
  active: T;
  onChange: (id: T) => void;
  className?: string;
}) {
  return (
    <div
      role="tablist"
      className={cn(
        'flex gap-1 overflow-x-auto overflow-y-hidden border-b border-line no-scrollbar',
        className,
      )}
    >
      {tabs.map((tab) => {
        const selected = tab.id === active;
        return (
          <button
            key={tab.id}
            role="tab"
            type="button"
            aria-selected={selected}
            onClick={() => onChange(tab.id)}
            className={cn(
              'relative shrink-0 px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors duration-[--duration-quick]',
              selected ? 'text-paper' : 'text-muted hover:text-paper',
              selected &&
                'after:absolute after:inset-x-2 after:-bottom-px after:h-0.5 after:rounded-full after:bg-gold',
            )}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span className="tabular ml-2 text-xs text-faint">{tab.count}</span>
            )}
            {tab.alerta !== undefined && tab.alerta > 0 && (
              <span
                className="tabular ml-2 inline-flex min-w-5 items-center justify-center rounded-full bg-gold px-1.5 py-0.5 text-[0.6875rem] leading-none font-bold text-black"
                aria-label={`${tab.alerta} ${tab.alerta === 1 ? 'pendente' : 'pendentes'}`}
              >
                {tab.alerta}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export * from './CampoDeSenha';
export * from './ConfirmModal';
export * from './DateTimePicker';
export * from './InfoDica';


